/* eslint-disable @typescript-eslint/no-explicit-any */
import { createElement, memo } from "react";
import styled from "styled-components";
import { Flex } from "../Box";
import isTouchDevice from "../../util/isTouchDevice";
import DropdownMenu from "../DropdownMenu/DropdownMenu";
import MenuItem from "../MenuItem/MenuItem";
import LiveLogos from "./LiveLogos";
import { MenuItemsProps } from "./types";

/** The featured destination (Swap) in the top bar: the app's primary colour, as a pill, so it reads as the main action. */
const FeaturedPill = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.invertedContrast};
  background: ${({ theme }) => theme.colors.primary};
  box-shadow: ${({ $active, theme }) => ($active ? `0 0 0 2px ${theme.colors.background}, 0 0 0 4px ${theme.colors.primary}` : "none")};
  transition: filter 150ms ease-out;
  &:hover {
    filter: brightness(1.08);
  }
  svg {
    fill: currentColor;
  }
`;

const MenuItems: React.FC<React.PropsWithChildren<MenuItemsProps>> = ({
  items = [],
  activeItem,
  activeSubItem,
  ...props
}) => {
  return (
    <Flex {...props}>
      {items.map(({ label, ariaLabel, featured, items: menuItems = [], href, icon, disabled, live }) => {
        const statusColor = menuItems?.find((menuItem) => menuItem.status !== undefined)?.status?.color;
        const isActive = activeItem === href;
        const linkProps = isTouchDevice() && menuItems && menuItems.length > 0 ? {} : { href };
        const Icon = icon;
        return (
          <DropdownMenu
            key={`${label}#${href}`}
            items={menuItems}
            py={1}
            activeItem={activeSubItem}
            isDisabled={disabled}
          >
            {live !== undefined ? (
              // a tab that can be live (Competitions): its link, then the live logos' reserved slot, a link of its own beside it
              <Flex alignItems="center">
                <MenuItem {...linkProps} isActive={isActive} statusColor={statusColor} isDisabled={disabled} aria-label={label ? undefined : ariaLabel}>
                  {label}
                </MenuItem>
                <LiveLogos live={live} place="bar" />
              </Flex>
            ) : (
              <MenuItem {...linkProps} isActive={isActive} statusColor={statusColor} isDisabled={disabled} aria-label={label ? undefined : ariaLabel}>
                {featured ? (
                  <FeaturedPill $active={isActive}>
                    {icon && createElement(Icon as any, { width: "20px", color: "currentColor" })}
                    {label}
                  </FeaturedPill>
                ) : (
                  label || (icon && createElement(Icon as any, { color: isActive ? "secondary" : "textSubtle" }))
                )}
              </MenuItem>
            )}
          </DropdownMenu>
        );
      })}
    </Flex>
  );
};

export default memo(MenuItems);
